import React from 'react'
import { Search, RotateCcw } from 'lucide-react'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import type { MrpDashboardFilter } from '../types/mrpTypes'

export interface MrpFiltersBarProps {
  filter: MrpDashboardFilter
  onChange: (next: MrpDashboardFilter) => void
  onReset: () => void
}

export function MrpFiltersBar({
  filter,
  onChange,
  onReset,
}: MrpFiltersBarProps): React.ReactElement {
  const isFiltered =
    filter.search.trim() !== '' ||
    filter.shortage_only ||
    filter.demand_type !== 'ALL' ||
    filter.issue_filter !== 'ALL'

  return (
    <PageToolbar
      left={
        <div className="flex flex-wrap items-center gap-2">
          {/* Search box */}
          <div className="relative min-w-[240px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filter.search}
              onChange={(e) => onChange({ ...filter, search: e.target.value })}
              placeholder="Tìm vật tư, mã, quy cách..."
              className="w-full rounded border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Demand type filter */}
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="font-medium text-slate-500">Nguồn:</span>
            <select
              value={filter.demand_type}
              onChange={(e) => onChange({ ...filter, demand_type: e.target.value })}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:outline-none"
            >
              <option value="ALL">Tất cả nguồn nhu cầu</option>
              <option value="PRODUCTION">Sản xuất (PRODUCTION)</option>
              <option value="PURCHASING">Mua hàng ngoài (PURCHASING)</option>
            </select>
          </div>

          {/* Issue / warning filter */}
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="font-medium text-slate-500">Trạng thái:</span>
            <select
              value={filter.issue_filter}
              onChange={(e) =>
                onChange({
                  ...filter,
                  issue_filter: e.target.value as MrpDashboardFilter['issue_filter'],
                })
              }
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:outline-none"
            >
              <option value="ALL">Tất cả trạng thái</option>
              <option value="ISSUES_ONLY">Chỉ dòng có cảnh báo / lỗi</option>
              <option value="NORMAL_ONLY">Chỉ dòng chuẩn xác</option>
            </select>
          </div>
        </div>
      }
      right={
        <div className="flex items-center gap-3">
          {/* Shortage only toggle checkbox */}
          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-50 border border-transparent hover:border-rose-200">
            <input
              type="checkbox"
              checked={filter.shortage_only}
              onChange={(e) => onChange({ ...filter, shortage_only: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
            />
            <span>Chỉ hiện thiếu hàng</span>
          </label>

          {isFiltered && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onReset}
              className="gap-1 text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Đặt lại
            </Button>
          )}
        </div>
      }
    />
  )
}
